import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { t } from 'i18next';
import React, { FC, useContext, useEffect, useState } from 'react';
import CollapsableTable from './collapsableTable/collapsableTable';
import styles from './usersTableRow.module.scss';
import { Permiso, Usuario } from '../../../../types/Admin';
import ObrasAPI from '../../../../services/APIObras';
import { AdminContext, EmailandName } from '../../../../context/AdminProvider';
import { Button, Dialog, Stack, SvgIcon } from '@mui/material';
import { ReactComponent as error } from '../../../../assets/error.svg';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface UsersTableRowProps {
	user: Usuario;
  index: number;
  userInput: string;
  canManageUserRoles: boolean;
 }

export const renderRole = (role: string) => {
  switch(role) {
    case 'viewer':
      return t('administration.table.viewer');
    case 'viewer_vip':
      return t('administration.table.vip');
    case 'editor':
      return t('administration.table.editor');
    case 'validator':
      return t('administration.table.validator');
    case 'administrator':
      return t('administration.table.admin');
    case 'download':
      return t('administration.table.download');
  }
};

const MANAGEABLE_GROUPS = new Set(['viewer', 'viewer_vip', 'editor', 'validator', 'administrator', 'download']);
const hasManageableGroups = (groups: string[]) => groups.some((group) => MANAGEABLE_GROUPS.has(group));
// objectCode is used for editing; a complete nomProyecto from the API needs no
// second lookup. The fallback keeps old/partial tuples readable during rollout.
const needsProjectLabelResolution = (permission: Permiso) => permission.objectType === 'project'
  && (!permission.objectCode
    || permission.nomProyecto.trim() === permission.objectCode.trim()
    || permission.nomProyecto.trim() === `${permission.objectCode}-${permission.codEmpresa || '0008'}`);

/* eslint-disable @typescript-eslint/no-non-null-assertion */
const UsersTableRow: FC<UsersTableRowProps> = (props) => {
  const { user, index, userInput, canManageUserRoles } = props;
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [permisos, setPermisos] = useState<Permiso[]>([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(!hasManageableGroups(user.grupos));
  const api = ObrasAPI.getInstance();
  const adminContext = useContext(AdminContext);
  const updatedPermissions = adminContext.updatedPermissions ? adminContext.updatedPermissions[user.email] : undefined;
  const [groups, setGroups] = useState<string[]>(user.grupos);
  const [openError, setOpenError] = useState(false);

  useEffect(() => {
    setGroups(user.grupos);
    setPermisos([]);
    setPermissionsLoaded(!hasManageableGroups(user.grupos));
  }, [user]);
 
  useEffect(() => {
    setOpen(false);
  }, [userInput]);

  const deleteRole = async (id: string | undefined, email: string, grupo: string | undefined, index: number) => {
    if(id && grupo){
      setLoading(true);
      const response = await api.deleteRole(id, email);
      if (response.error) {
        setOpenError(true);
      } else {
        setGroups(response.groups);
        setPermisos(permisos.filter((_perm, i) => i !== index));
      }
      setLoading(false);
    }
  };

  const handleClickRow = () => {
    if (!open && !permissionsLoaded) {
      setLoading(true);
      api.getPermissionsByUserEmail(user.email)
        .then((result) => {
          if (!Array.isArray(result)) {
            setOpenError(true);
            setLoading(false);
            return;
          }
          const projectIds = Array.from(new Set<string>(result
            .filter(needsProjectLabelResolution)
            .map((permission: Permiso) => permission.objectId)));
          setPermisos(result.map((permission: Permiso) => ({
            ...permission,
            labelLoading: projectIds.includes(permission.objectId),
          })));
          setGroups(Array.from(new Set<string>([
            ...result.map((permission: Permiso) => permission.rol),
            ...(user.viewerVipAssignmentCount ? ['viewer_vip'] : []),
          ])));
          setPermissionsLoaded(true);
          setLoading(false);

          if (projectIds.length > 0) {
            api.getPermissionProjectLabels(projectIds)
              .then((labels) => {
                setPermisos(current => current.map((permission) => ({
                  ...permission,
                  nomProyecto: labels[permission.objectId] || permission.nomProyecto,
                  labelLoading: false,
                })));
              })
              .catch((error) => {
                console.error('Unable to resolve WANDA project labels', error);
                setPermisos(current => current.map((permission) => ({
                  ...permission,
                  labelLoading: false,
                })));
              });
          }
        }).catch(err => {
          console.log(err);
          setOpenError(true);
          setLoading(false);
        });
    }
    setOpen(!open);
  };

  const handleEditUser = () => {
    const userName = [...adminContext.allUsers].find((u) => u.email === user.email)?.nombre;
    const userToEdit: EmailandName = {email: user?.email, name:userName};
    if (userName) {
      adminContext.handleChangeUserToEdit(userToEdit);
      adminContext.handleChangePermissionToEdit(undefined);
      adminContext.handleChangeHasProject(false);
      adminContext.handleSetIsEdition(true);
    }
  };

  const processRole = (role: string) => {
    const userGroups = Array.from(new Set([
      ...(updatedPermissions || groups),
      ...(user.viewerVipAssignmentCount ? ['viewer_vip'] : []),
    ]));
    return (role === 'administrator' && userGroups.includes('administrator')) || 
      (role === 'viewer_vip' && userGroups.includes('viewer_vip') && !userGroups.includes('administrator')) ||
      (role !== 'administrator' && userGroups.includes(role) && !userGroups.includes('administrator')) ?
      <div
        key={role}
        className={`${styles[role]}`}
      >
        {renderRole(role)}
      </div>
      : null;
  };

  const renderErrorDelete = () => {
    return <Dialog
      classes={{
        paper: styles.dialogError
      }}
      BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.15)' } }}
      PaperProps={{ sx: { borderRadius: '20px', minWidth: '700px' } }}
      open={openError}
      onClose={() => setOpenError(false)}
    >
      <div className={styles.dialogContentError}>
        <div data-testid='error-test' className={styles.serverError}>
          <div className={styles.serverErrorContent}>
            <SvgIcon component={error} inheritViewBox />
            <div id='error-title' className={styles.title}>{t('administration.errorDeleteTitle')}</div>
            <div id='error-subtitle' className={styles.subtitle}>{t('administration.errorDescription')}</div>
          </div>
        </div>
        <Stack spacing={2} direction="row" justifyContent="center">
          <Button className={styles.buttonConfirm} onClick={() => {
            setOpenError(false);
          }} disableRipple={true} variant="contained">{t('administration.acceptButton')}</Button>
        </Stack>
      </div>
    </Dialog>;
  };

  return <div className={styles.tableRow}>
    {renderErrorDelete()}
    <div className={styles.tableRowParent}>
      <div className={styles.nameCellCollapsable} onClick={handleClickRow}>
        <div className={styles.nameContainer}>
          <div className={styles.nameContainerIcon}>
            {open ? <ArrowDropDownIcon /> : <ArrowRightIcon />}
          </div>
          <div className={styles.userIdentity}>
            <span className={styles.userName}>{user.nombre}</span>
            <span className={styles.userEmail}>{user.email}</span>
          </div>
        </div>
      </div>
      <div className={styles.rolesCell}>
        <div className={styles.rolesWrapper}>
          { processRole('viewer') }
          { processRole('viewer_vip') }
          { processRole('editor') }
          { processRole('validator') }
          { processRole('download') }
          { processRole('administrator') }
        </div>
      </div>
      <div className={styles.addRoleCell}>
        {canManageUserRoles && <div
          id={`addRoleBtn${index}`}
          className={styles.editBtn}
          onClick={handleEditUser}
        > + {t('administration.table.addRole')}
        </div>}
      </div>
    </div>
    {permisos &&
      <div className={styles.tableRowChild}>
        <CollapsableTable
          open={open}
          loading={loading}
          permissions={permisos}
          user={user}
          deleteRole={deleteRole}
          canManageUserRoles={canManageUserRoles}
        />
      </div>
    }
  </div>;
};
export default UsersTableRow;
